import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { TemplateGallery } from './TemplateGallery';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import type { TemplateCatalog } from '../../types/template';
import { Input } from '@/components/ui/input';

export interface TemplateBrowserProps {
  selectedTemplate?: TemplateCatalog | null;
  /** Called when the user picks a template. */
  onTemplateSelect: (template: TemplateCatalog) => void;
  organizationId?: string;
}

export const TemplateBrowser: React.FC<TemplateBrowserProps> = ({
  selectedTemplate = null,
  onTemplateSelect,
  organizationId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateCatalog | null>(null);

  // Debounce search input into the URL query param + the TemplateGallery fetch.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);

      const next = new URLSearchParams(searchParams);
      if (searchQuery.trim()) {
        next.set('search', searchQuery.trim());
      } else {
        next.delete('search');
      }
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleTemplateSelect = useCallback(
    (template: TemplateCatalog) => {
      setPreviewTemplate(null);
      onTemplateSelect(template);
    },
    [onTemplateSelect],
  );

  const handleTemplatePreview = useCallback((template: TemplateCatalog) => {
    setPreviewTemplate(template);
  }, []);

  const hasFilters = !!debouncedSearch;

  return (
    <div className="flex flex-col gap-5 flex-1 min-h-0">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<Search className="h-4 w-4" strokeWidth={1.5} />}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <TemplateGallery
          onTemplateSelect={handleTemplateSelect}
          onTemplatePreview={handleTemplatePreview}
          organizationId={organizationId}
          search={debouncedSearch}
          selectedTemplateId={selectedTemplate?.id}
          hasFilters={hasFilters}
        />
      </div>

      <TemplatePreviewDialog
        open={!!previewTemplate}
        template={previewTemplate}
        onOpenChange={(open) => {
          if (!open) setPreviewTemplate(null);
        }}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
};

export default TemplateBrowser;
